
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  Card,
  CardMedia,
  Skeleton,
  Box,
  Typography,
} from "@mui/material";

// ------------------------------------
// Shared IntersectionObserver (single instance)
// ------------------------------------
let __io;
const __registry = new Map();

const ensureObserver = () => {
  if (__io) return __io;

  __io = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        const setter = __registry.get(entry.target);

        if (setter && entry.isIntersecting) {
          setter(true);
        }
      }
    },
    {
      root: null,
      rootMargin: "500px 0px",
      threshold: 0.01,
    }
  );

  return __io;
};

const useSharedInView = () => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const obs = ensureObserver();

    __registry.set(el, setInView);
    obs.observe(el);

    return () => {
      __registry.delete(el);
      try {
        obs.unobserve(el);
      } catch {}
    };
  }, []);

  return [ref, inView];
};

const isHttpish = (u) =>
  typeof u === "string" && /^https?:\/\//i.test(u);

const isLocalBlobUrl = (u) =>
  typeof u === "string" && u.startsWith("blob:");

const isDisplayableImageUrl = (u) =>
  isHttpish(u) || isLocalBlobUrl(u);

// ---------------- Media card (memoized) ----------------
const MediaCard = React.memo(
  ({
    item,
    isPlanner,
    canDelete = false,
    onDelete,
    onPhotoClick,
    showUploaderName,
    eager = false,
    priority = false,
  }) => {
    // Split load/fail state for image vs video so video errors
    // do not blank the still thumbnail.
    const [imgLoaded, setImgLoaded] = useState(false);
    const [imgFailed, setImgFailed] = useState(false);
    const [vidLoaded, setVidLoaded] = useState(false);
    const [vidFailed, setVidFailed] = useState(false);
    const [imgSourceStep, setImgSourceStep] = useState(0);
    const [imgRetryKey, setImgRetryKey] = useState(0);

    const [hostRef, inView] = useSharedInView();

    // ✅ Cached-image safety ref
    const imgRef = useRef(null);

    const isPhoto = item.type === "photo";
    const isVideo = item.type === "video";

    const thumbnailSrc = isDisplayableImageUrl(
      item.thumbnailUrl
    )
      ? item.thumbnailUrl
      : null;

    const fullSrc = isDisplayableImageUrl(item.url)
      ? item.url
      : null;

    const gridSrc = useMemo(() => {
      const baseSrc =
        imgSourceStep === 0 && thumbnailSrc
          ? thumbnailSrc
          : fullSrc || thumbnailSrc;

      if (
        !baseSrc ||
        imgRetryKey === 0 ||
        isLocalBlobUrl(baseSrc)
      ) {
        return baseSrc;
      }

      const separator = baseSrc.includes("?") ? "&" : "?";
      return `${baseSrc}${separator}retry=${imgRetryKey}`;
    }, [
      thumbnailSrc,
      fullSrc,
      imgSourceStep,
      imgRetryKey,
    ]);

    const hasImg =
      isPhoto && isDisplayableImageUrl(gridSrc);

    const hasVid = isVideo && isHttpish(item.url);

    useEffect(() => {
      setImgSourceStep(0);
      setImgRetryKey(0);
    }, [item.id, item.thumbnailUrl, item.url]);

    // Remote boomerang is playable whenever a boomerang URL exists.
    const boomerangRemotePlayable =
      isPhoto &&
      !!item.isBoomerang &&
      isHttpish(item.boomerangUrl) &&
      item.boomerangStatus !== "error";

    // Local boomerang playback using the blob URL from the camera flow.
    const boomerangLocalPlayable =
      isPhoto &&
      !!item.isBoomerang &&
      isLocalBlobUrl(item.boomerangLocalUrl);

    const boomerangVideoSrc = boomerangRemotePlayable
      ? item.boomerangUrl
      : boomerangLocalPlayable
      ? item.boomerangLocalUrl
      : null;

    const boomerangProcessing = false;

    // ✅ Reset load state only if the underlying source ACTUALLY changes.
    // Do not reset after the initial mount, which can race a cached image's onLoad.
    const resolvedVideoSrc =
      boomerangVideoSrc ||
      (isVideo ? item.url : null);

    const previousSourcesRef = useRef({
      gridSrc,
      resolvedVideoSrc,
    });

    useEffect(() => {
      const previous =
        previousSourcesRef.current;

      const imageSourceChanged =
        previous.gridSrc !== gridSrc;

      const videoSourceChanged =
        previous.resolvedVideoSrc !==
        resolvedVideoSrc;

      previousSourcesRef.current = {
        gridSrc,
        resolvedVideoSrc,
      };

      if (imageSourceChanged) {
        setImgLoaded(false);
        setImgFailed(false);
      }

      if (videoSourceChanged) {
        setVidLoaded(false);
        setVidFailed(false);
      }
    }, [gridSrc, resolvedVideoSrc]);

    // Eager first-screen cards load immediately.
    // Remaining cards still wait for IntersectionObserver.
    const shouldLoadMedia = eager || inView;

    useEffect(() => {
      const boomerangVideoIsHealthy =
        !!boomerangVideoSrc &&
        vidLoaded &&
        !vidFailed;

      if (
        !shouldLoadMedia ||
        !hasImg ||
        imgLoaded ||
        imgFailed ||
        boomerangVideoIsHealthy
      ) {
        return;
      }

      const canUseFullImage =
        imgSourceStep === 0 &&
        thumbnailSrc &&
        fullSrc &&
        thumbnailSrc !== fullSrc;

      const timeout = setTimeout(
        () => {
          if (canUseFullImage) {
            setImgLoaded(false);
            setImgFailed(false);
            setImgSourceStep(1);
            return;
          }

          setImgLoaded(false);
          setImgFailed(true);
        },
        canUseFullImage ? 10000 : 15000
      );

      return () => clearTimeout(timeout);
    }, [
      shouldLoadMedia,
      hasImg,
      imgLoaded,
      imgFailed,
      imgSourceStep,
      thumbnailSrc,
      fullSrc,
      gridSrc,
      boomerangVideoSrc,
      vidLoaded,
      vidFailed,
    ]);

    // ✅ If a cached image completed before React caught onLoad,
    // detect the already-loaded DOM image and remove the skeleton.
    useEffect(() => {
      if (!shouldLoadMedia || !hasImg) return;

      const img = imgRef.current;

      if (img?.complete && img.naturalWidth > 0) {
        setImgFailed(false);
        setImgLoaded(true);
      }
    }, [shouldLoadMedia, hasImg, gridSrc]);

    const showBoomerangVideo =
      shouldLoadMedia &&
      !!boomerangVideoSrc &&
      !vidFailed;

    const showStillImage =
      shouldLoadMedia &&
      hasImg &&
      (!boomerangVideoSrc || !vidLoaded || vidFailed);

    const showingVideo =
      showBoomerangVideo ||
      (shouldLoadMedia && hasVid && !boomerangVideoSrc);

    const loaded = showingVideo ? vidLoaded : imgLoaded;
    const failed = showingVideo ? vidFailed : imgFailed;

    const hideBrokenForGuests =
      !isPlanner &&
      ((isPhoto &&
        imgFailed &&
        (!boomerangVideoSrc || vidFailed)) ||
        (isVideo && vidFailed));

    // Hide items that have no renderable media source.
    if (
      !isPlanner &&
      !hasImg &&
      !hasVid &&
      !boomerangVideoSrc
    ) {
      return null;
    }

    return (
      <Card
        ref={hostRef}
        sx={{
          position: "relative",
          m: "4px",
          overflow: "hidden",
          aspectRatio: "1/1",
          display: hideBrokenForGuests ? "none" : undefined,
        }}
      >
        {!loaded && !failed && (
          <Skeleton
            variant="rectangular"
            width="100%"
            height="100%"
            animation="wave"
            sx={{
              position: "absolute",
              inset: 0,
              zIndex: 2,
              pointerEvents: "none",
            }}
          />
        )}

        {/* Boomerang video */}
        {showBoomerangVideo && !vidFailed && (
          <CardMedia
            component="video"
            src={boomerangVideoSrc}
            key={boomerangVideoSrc}
            poster={hasImg ? gridSrc : undefined}
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            onLoadedData={(event) => {
              const video = event.currentTarget;

              video.muted = true;
              setVidFailed(false);
              setVidLoaded(true);

              const playPromise = video.play();

              if (playPromise?.catch) {
                playPromise.catch(() => {});
              }
            }}
            onError={() => {
              setVidFailed(true);
              setVidLoaded(false);
            }}
            sx={{
              position: "absolute",
              inset: 0,
              height: "100%",
              width: "100%",
              objectFit: "cover",
              opacity: vidLoaded ? 1 : 0,
              transition: "opacity .18s ease",
              backgroundColor: "#111",
              cursor: "pointer",
              zIndex: 1,
            }}
            onClick={onPhotoClick}
          />
        )}

        {/* Still image fallback */}
        {showStillImage && !imgFailed && (
          <CardMedia
            key={gridSrc}
            ref={imgRef}
            component="img"
            src={gridSrc}
            loading="eager"
            decoding="async"
            fetchPriority={priority ? "high" : "auto"}
            data-priority={priority ? "true" : undefined}
            sizes="(max-width: 600px) 50vw, 33vw"
            onLoad={() => {
              setImgFailed(false);
              setImgLoaded(true);
            }}
            onError={() => {
              const canUseFullImage =
                imgSourceStep === 0 &&
                thumbnailSrc &&
                fullSrc &&
                thumbnailSrc !== fullSrc;

              if (canUseFullImage) {
                setImgLoaded(false);
                setImgFailed(false);
                setImgSourceStep(1);
                return;
              }

              setImgLoaded(false);
              setImgFailed(true);
            }}
            sx={{
              position: "absolute",
              inset: 0,
              height: "100%",
              width: "100%",
              objectFit: "cover",
              opacity: imgLoaded ? 1 : 0,
              transition: "opacity .18s ease",
              cursor: "pointer",
              display: "block",
              zIndex: 1,
            }}
            onClick={onPhotoClick}
          />
        )}

        {/* Normal videos */}
        {shouldLoadMedia &&
          hasVid &&
          !boomerangVideoSrc &&
          !vidFailed && (
            <CardMedia
              component="video"
              src={item.url}
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
              onLoadedData={() => {
                setVidFailed(false);
                setVidLoaded(true);
              }}
              onError={() => {
                setVidFailed(true);
                setVidLoaded(false);
              }}
              sx={{
                position: "absolute",
                inset: 0,
                height: "100%",
                width: "100%",
                objectFit: "cover",
                opacity: vidLoaded ? 1 : 0,
                transition: "opacity .18s ease",
                backgroundColor: "#111",
                cursor: "pointer",
                zIndex: 1,
              }}
              onClick={onPhotoClick}
            />
          )}

        {/* Processing badge for boomerangs */}
        {boomerangProcessing && (
          <Box
            sx={{
              position: "absolute",
              top: 8,
              left: 8,
              zIndex: 6,
              px: 1,
              py: 0.5,
              borderRadius: 999,
              fontSize: 12,
              fontWeight: 700,
              color: "#fff",
              bgcolor: "rgba(0,0,0,0.55)",
              backdropFilter: "blur(8px)",
              WebkitBackdropFilter: "blur(8px)",
              border: "1px solid rgba(255,255,255,0.14)",
              pointerEvents: "none",
            }}
          >
            Boomerang processing…
          </Box>
        )}

        {showUploaderName && item.uploaderName && (
          <Box
            sx={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              px: 1.5,
              pt: 4,
              pb: 0.55,
              background:
                "linear-gradient(to top, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.28) 48%, rgba(0,0,0,0) 100%)",
              pointerEvents: "none",
              zIndex: 6,
            }}
          >
            <Typography
              sx={{
                fontFamily: '"Alex Brush","Great Vibes",cursive',
                fontWeight: 400,
                fontSize: {
                  xs: "clamp(19px, 4.8vw, 27px)",
                  sm: "27px",
                },
                lineHeight: 1,
                color: "#fff",
                textShadow:
                  "0 1px 3px rgba(0,0,0,0.55), 0 0 1px rgba(0,0,0,0.35)",
                letterSpacing: "0.01em",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
              title={item.uploaderName}
            >
              {item.uploaderName}
            </Typography>
          </Box>
        )}

        {((isPhoto &&
          imgFailed &&
          (!boomerangVideoSrc || vidFailed)) ||
          (isVideo && vidFailed)) && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 9,
              pointerEvents: "auto",
              bgcolor: "rgba(0,0,0,0.08)",
              color: "#fff",
              cursor: "pointer",
            }}
            onClick={(e) => {
              e.stopPropagation();
              setImgFailed(false);
              setImgLoaded(false);
              setVidFailed(false);
              setVidLoaded(false);
              setImgSourceStep(fullSrc ? 1 : 0);
              setImgRetryKey((current) => current + 1);
            }}
            title="Tap to retry"
            role="button"
            aria-label="Retry loading media"
          >
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "rgba(0,0,0,0.42)",
                backdropFilter: "blur(6px)",
              }}
            >
              <Box
                component="svg"
                viewBox="0 0 24 24"
                aria-hidden="true"
                sx={{
                  width: 18,
                  height: 18,
                }}
              >
                <path
                  d="M20 11a8 8 0 1 0-2.34 5.66"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <path
                  d="M20 5v6h-6"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </Box>
            </Box>
          </Box>
        )}
      </Card>
    );
  },
  (prev, next) => {
    const a = prev.item;
    const b = next.item;

    return (
      a.id === b.id &&
      a.url === b.url &&
      a.thumbnailUrl === b.thumbnailUrl &&
      a.type === b.type &&
      a.uploaderName === b.uploaderName &&
      a.isBoomerang === b.isBoomerang &&
      a.boomerangUrl === b.boomerangUrl &&
      a.boomerangLocalUrl === b.boomerangLocalUrl &&
      a.boomerangStatus === b.boomerangStatus &&
      prev.isPlanner === next.isPlanner &&
      prev.canDelete === next.canDelete &&
      prev.showUploaderName === next.showUploaderName &&
      prev.eager === next.eager &&
      prev.priority === next.priority
    );
  }
);

export default MediaCard;
